/**
 * DEC-001: Shopify-style Experience View for Sales Order (Ecommerce Context)
 *
 * This view creates a Shopify-like order cockpit for ecommerce Sales Orders.
 * 
 * R-SHOP-1: Header summary with order ID, status, dates, and totals
 * R-SHOP-2: Info architecture matching Shopify's left/right column mental model
 * R-SHOP-3: Clean items section (columns controlled by ERPNext meta in_list_view)
 * R-SHOP-4: Relation widgets for fulfillment & payment tracking
 * R-SHOP-5: Context-specific (ecommerce only)
 * R-SHOP-6: Fully config-driven (no component hacks)
 * 
 * IMPORTANT NOTES:
 * 
 * 1. Fieldname validation:
 *    - All fieldnames are validated against meta in dev mode
 *    - Fields that may need adjustment:
 *      - delivery_status: May not exist; using 'status' as fallback
 *      - shipping_address_display: May be computed field
 *      - rounded_total: May not exist in all ERPNext versions
 * 
 * 2. Custom link fields required:
 *    Relation widgets require custom Link fields on related doctypes:
 *    - Pick List:      zvg_sales_order (Link → Sales Order)
 *    - Delivery Note:  zvg_sales_order (Link → Sales Order)
 *    - Sales Invoice:  zvg_sales_order (Link → Sales Order)
 *    - Payment Entry:  zvg_sales_order (Link → Sales Order)
 * 
 * 3. Items table columns:
 *    For Shopify-style clean line items, ensure Sales Order Item meta has
 *    these fields in in_list_view:
 *    - item_code, item_name, qty, rate, amount
 *    Full details remain available in edit modal.
 */

import type { DocTypeView } from '../types';

export const salesOrderShopifyEcomView: DocTypeView = {
  id: 'sales-order-shopify-ecom-v1',
  doctype: 'Sales Order',
  style: 'shopify',          // → maps to apple-style layout
  context: 'ecommerce',
  status: 'active',
  label: 'Sales Order – Ecommerce (Shopify Style)',
  description: 'Shopify-style order cockpit for ecommerce Sales Orders. Includes header summary, customer/shipping cards, line items, totals, and fulfillment/payment relation widgets.',

  formView: {
    sections: [
      // 1. Order Info — compact key-value card (who, when, how)
      // Customer is rendered by ShopifyOrderHeader widget, not here
      {
        id: 'order-overview',
        title: 'Order Info',
        description: 'Key order dates and context.',
        layout: [
          {
            columns: [
              {
                fieldname: 'transaction_date',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'delivery_date',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'source_channel',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'payment_method_type',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'order_notes',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 2. Customer Card (Shopify-style read-only card, click to edit)
      // R-SHOP-2: Customer card with identity and channel info
      {
        id: 'customer-channel',
        title: 'Customer',
        description: 'Customer information and order source.',
        layout: [
          {
            columns: [
              {
                fieldname: 'customer',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'customer_name',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'customer_group',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'territory',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'po_no',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 2b. Marketplace Reference (shown for Lazada/TikTok orders)
      // Displays the source platform order number for cross-referencing
      {
        id: 'marketplace',
        title: 'Marketplace',
        description: 'Marketplace channel and external order reference.',
        layout: [
          {
            columns: [
              {
                fieldname: 'source_channel',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'lazada_order_id',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'lazada_order_number',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'lazada_trade_order_id',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'tiktok_order_id',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'tiktok_order_number',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 3. Billing Address (own card)
      // Uses ERPNext display field for billing address HTML
      {
        id: 'billing-address',
        title: 'Billing Address',
        description: 'Billing address for this order.',
        layout: [
          {
            columns: [
              {
                fieldname: 'address_display', // ERPNext billing address display (HTML)
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 4. Shipping Address (own card)
      // Uses ERPNext shipping address field (Small Text, HTML formatted)
      {
        id: 'shipping-address',
        title: 'Shipping Address',
        description: 'Shipping address for this order.',
        layout: [
          {
            columns: [
              {
                fieldname: 'shipping_address', // ERPNext shipping address field (Small Text, HTML formatted)
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 5. Address & Contact (contact person + contact info only)
      // R-SHOP-2: Contact information (addresses are separate above)
      {
        id: 'address-contact',
        title: 'Address & Contact',
        description: 'Customer contact information.',
        layout: [
          {
            columns: [
              {
                fieldname: 'contact_person',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'contact_display',
                mode: 'read',
                visibility: 'default',
                width: '2/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'contact_email',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'contact_mobile',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'contact_phone',
                mode: 'read',
                visibility: 'advanced',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 6. Shipping & Fulfillment Card (Shopify-style read-only card, click to edit)
      // R-SHOP-2: Fulfillment card - warehouse and status only (addresses are separate)
      {
        id: 'shipping-fulfillment',
        title: 'Shipping & Fulfillment',
        description: 'Fulfillment warehouse and delivery status.',
        layout: [
          {
            columns: [
              {
                fieldname: 'set_warehouse',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                // NOTE: This field may not exist in all ERPNext versions
                // If meta validation shows it missing, remove or use 'status' instead
                // V1: Using status for fulfillment state (can enhance later)
                fieldname: 'status',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 7. Currency & Price List (Shopify-style read-only card, click to edit)
      {
        id: 'currency-price-list',
        title: 'Currency & Price List',
        description: 'Currency and pricing configuration for this order.',
        layout: [
          {
            columns: [
              {
                fieldname: 'currency',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'price_list',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'selling_price_list',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'plc_conversion_rate',
                mode: 'read',
                visibility: 'advanced',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 8. Items (Left column primary - Shopify-style line items)
      // R-SHOP-3: Items section - clean line items table
      // Note: Columns are controlled by ERPNext meta in_list_view for Sales Order Item
      // Ensure meta has: item_code, item_name, qty, rate, amount in in_list_view
      {
        id: 'items',
        title: 'Items',
        description: 'Order line items. Full details available in edit modal.',
        layout: [
          {
            columns: [
              {
                fieldname: 'items', // Child table (Sales Order Item)
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 9. Additional Discounts (Shopify-style read-only card, click to edit)
      {
        id: 'discounts',
        title: 'Additional Discounts',
        description: 'Discount and promotional adjustments applied to the order.',
        layout: [
          {
            columns: [
              {
                fieldname: 'discount_amount',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'additional_discount_percentage',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'additional_discount_amount',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'discount_percentage',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'apply_discount_on',
                mode: 'read',
                visibility: 'advanced',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 10. Order Summary — receipt-style totals (directly below items)
      // Shows subtotal → discount → shipping → tax → grand total in one card
      {
        id: 'totals',
        title: 'Order Summary',
        description: 'Complete order total breakdown — items, discounts, shipping, taxes.',
        layout: [
          {
            columns: [
              {
                fieldname: 'total',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'discount_amount',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'shipping_rule',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'total_taxes_and_charges',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'grand_total',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },


      // 9. Taxes & Charges (Tax templates and settings)
      // For Details tab - shows tax configuration
      {
        id: 'taxes_section',
        title: 'Taxes & Charges',
        description: 'Tax templates and calculated charges for this order.',
        layout: [
          {
            columns: [
              {
                fieldname: 'taxes_and_charges',
                mode: 'edit',  // Allow user to select tax template
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'tax_category',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                // Virtual toggle — sets included_in_print_rate on ALL tax rows.
                // Derived from taxes[] state; handler in SalesOrderDetailPage.
                fieldname: 'included_in_print_rate',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
                virtualFieldDef: {
                  fieldtype: 'Check',
                  label: 'Price Includes Tax (VAT)',
                  description: 'When enabled, item prices already include tax. The system will reverse-calculate the net amount and tax breakdown.',
                },
              },
              {
                fieldname: 'taxes',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 10. Shipping & Incoterms
      // For Details tab - shipping rules and international terms
      {
        id: 'shipping_section',
        title: 'Shipping & Delivery',
        description: 'Shipping rules and international commercial terms.',
        layout: [
          {
            columns: [
              {
                fieldname: 'shipping_rule',
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'incoterm',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'named_place',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 11. Terms & Conditions (Shopify-style read-only card, click to edit)
      // For Terms tab
      {
        id: 'terms',
        title: 'Terms & Conditions',
        description: 'Payment terms and conditions for this order.',
        layout: [
          {
            columns: [
              {
                fieldname: 'payment_terms_template',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'payment_terms',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'tc_name',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'terms',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 10. Commission (ERPNext commission data)
      // Commission fields verified in meta: amount_eligible_for_commission, commission_rate, total_commission
      {
        id: 'commission',
        title: 'Commission',
        description: 'Commission eligibility, rate, and totals.',
        layout: [
          {
            columns: [
              {
                fieldname: 'amount_eligible_for_commission',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'commission_rate',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'total_commission',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 11. Sales Team (ERPNext child table)
      // Fields verified from Sales Team doctype: sales_person, contact_no, allocated_percentage, allocated_amount, commission_rate, incentives
      {
        id: 'sales-team',
        title: 'Sales Team',
        description: 'Sales persons and their contribution, commission rate, and incentives.',
        layout: [
          {
            columns: [
              {
                fieldname: 'sales_team', // Child table: Sales Team
                mode: 'edit',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
        ],
      },

      // 12. Status Summary (pulled from ERPNext meta)
      // Includes overall status plus delivered/billed/picked percentages
      {
        id: 'status-summary',
        title: 'Status Summary',
        description: 'Order status and fulfillment/billing progress.',
        layout: [
          {
            columns: [
              {
                fieldname: 'status',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'delivery_status',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'per_delivered',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'per_billed',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'per_picked',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
        ],
      },

      // 13. More Information (Additional order details)
      // For More Info tab
      {
        id: 'more-info',
        title: 'More Information',
        description: 'Additional order information and metadata.',
        layout: [
          {
            columns: [
              {
                fieldname: 'campaign',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'source',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'order_type',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'company',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'transaction_date',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'delivery_date',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'po_no',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'po_date',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
              {
                fieldname: 'amended_from',
                mode: 'read',
                visibility: 'advanced',
                width: 'full',
              },
            ],
          },
        ],
      },
    ],

    customFieldBucket: {
      enabled: false,
      sectionTitle: 'Additional ERP Fields',
      visibilityDefault: 'collapsed',
    },

    // Relation widgets removed temporarily - will be re-added after fixing issues
  },

  createdBy: 'system',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

